import React, { useMemo, useState } from 'react';
import { Button, Collapse, Form, Input, Select, Space, Switch, Typography, Upload, notification } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { CategoriesProxy } from '../services/catalogServiceAPI/categoriesProxy';
import { categoryIconSrc } from './categoryIconSrc';

const { Text } = Typography;

export const TYPE_OPTIONS = [
    { value: 'items', label: 'Items (furniture & decor)' },
    { value: 'room', label: 'Walls & Floors' },
    { value: '', label: 'Hidden (no tab)' },
];

const SIZE_LEVEL_OPTIONS = [
    { value: 2, label: '2 — S | L' },
    { value: 3, label: '3 — S | M | L' },
    { value: 4, label: '4 — S | M | L | XL' },
    { value: 5, label: '5 — XS | S | M | L | XL' },
];

const ICON_HELP = 'An SVG with a square viewBox (the bundled icons use 56×56) drawn in #212121. '
    + 'Give stroke-only shapes the class "category-button-stroke" so the web can turn them white on press. '
    + 'Up to 64 KB; plain shapes and class styles only, no scripts, images or external references. '
    + 'It goes live on the next catalog load, no deploy needed.';

function descendantIds(categories, rootId) {
    const out = new Set();
    const stack = [rootId];
    while (stack.length) {
        const current = stack.pop();
        categories.forEach((c) => {
            if (c.parentId === current && !out.has(c.id)) {
                out.add(c.id);
                stack.push(c.id);
            }
        });
    }
    return out;
}

/**
 * Create or edit one category. `categories` is the full list (for the parent
 * picker); `category` is the row being edited, or undefined for a new one.
 * `onSubmit(values)` saves; it may throw to show the server's message.
 */
export function CategoryForm({ categories, category, onSubmit, submitLabel }) {
    const [form] = Form.useForm();
    const proxy = useMemo(() => new CategoriesProxy(), []);
    const [iconUrl, setIconUrl] = useState(category?.iconUrl ?? null);
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const excluded = category ? descendantIds(categories, category.id).add(category.id) : new Set();
    const parentOptions = categories
        .filter((c) => !excluded.has(c.id))
        .sort((a, b) => a.title.localeCompare(b.title))
        .map((c) => ({ value: c.id, label: c.title }));

    const uploadIcon = async (file) => {
        setUploading(true);
        try {
            const { url } = await proxy.uploadIcon(file);
            setIconUrl(url);
            form.setFieldValue('iconUrl', url);
        } catch (e) {
            notification.error({ message: 'Icon rejected', description: e.message });
        } finally {
            setUploading(false);
        }
        return false;
    };

    const clearIcon = () => {
        setIconUrl(null);
        form.setFieldValue('iconUrl', null);
    };

    const finish = async (values) => {
        setSubmitting(true);
        try {
            await onSubmit({
                ...values,
                iconUrl: iconUrl ?? null,
                icon: values.icon ? values.icon.trim() : null,
                priority: values.priority === '' || values.priority === undefined ? null : Number(values.priority),
                parentId: values.parentId ?? null,
            });
        } catch (e) {
            notification.error({ message: 'Could not save category', description: e.message });
        } finally {
            setSubmitting(false);
        }
    };

    const legacyIconSrc = Form.useWatch('icon', form);
    const previewSrc = iconUrl ?? categoryIconSrc({ icon: legacyIconSrc });

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={finish}
            requiredMark={false}
            initialValues={{
                title: category?.title,
                type: category ? category.type ?? '' : 'items',
                parentId: category?.parentId ?? undefined,
                priority: category?.priority ?? undefined,
                icon: category?.icon ?? undefined,
                iconUrl: category?.iconUrl ?? null,
                sizeFilterEnabled: category?.sizeFilterEnabled ?? false,
                sizeLevels: category?.sizeLevels ?? 3,
            }}
        >
            <Form.Item label="Title" name="title" rules={[{ required: true, message: 'Give the category a title' }]}>
                <Input maxLength={120} />
            </Form.Item>
            <Form.Item label="Icon" extra={ICON_HELP}>
                <Space align="center" size="middle">
                    <div style={{ width: 56, height: 56, display: 'grid', placeItems: 'center', background: '#fcfdfd', border: '1px solid #e8e8e8', borderRadius: 12 }}>
                        {previewSrc
                            ? <img src={previewSrc} alt="" style={{ width: 36, height: 36 }} />
                            : <Text type="secondary" style={{ fontSize: 11 }}>none</Text>}
                    </div>
                    <Upload accept=".svg,image/svg+xml" showUploadList={false} beforeUpload={uploadIcon}>
                        <Button icon={<UploadOutlined />} loading={uploading}>{iconUrl ? 'Replace SVG' : 'Upload SVG'}</Button>
                    </Upload>
                    {iconUrl && <Button type="link" danger onClick={clearIcon}>Remove</Button>}
                </Space>
                <Form.Item name="iconUrl" hidden><Input /></Form.Item>
            </Form.Item>
            <Form.Item label="Type" name="type" extra="Only matters for top-level categories: it picks the catalog tab the category appears in. Hidden shows it in no tab.">
                <Select options={TYPE_OPTIONS} />
            </Form.Item>
            <Form.Item label="Parent category" name="parentId">
                <Select options={parentOptions} allowClear showSearch optionFilterProp="label" placeholder="None (top level)" />
            </Form.Item>
            <Form.Item label="Priority" name="priority" extra="Lower numbers come first.">
                <Input type="number" min={0} step={1} />
            </Form.Item>
            <Form.Item
                label="Size filter"
                name="sizeFilterEnabled"
                valuePropName="checked"
                extra="Shows an S/M/L filter when browsing this category. Buckets appear after the next apply-cuts run + reindex."
            >
                <Switch />
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, next) => prev.sizeFilterEnabled !== next.sizeFilterEnabled}>
                {({ getFieldValue }) => getFieldValue('sizeFilterEnabled') && (
                    <Form.Item label="Size buckets" name="sizeLevels">
                        <Select options={SIZE_LEVEL_OPTIONS} />
                    </Form.Item>
                )}
            </Form.Item>
            <Collapse
                ghost
                style={{ marginBottom: 16 }}
                items={[{
                    key: 'advanced',
                    label: 'Advanced',
                    children: (
                        <Form.Item
                            label="Legacy icon key"
                            name="icon"
                            extra="Name of an icon bundled with the web app (static/assets/icons/categories). Only app versions from before uploaded icons read it; new categories should upload an SVG instead."
                        >
                            <Input placeholder="e.g. sofa" />
                        </Form.Item>
                    ),
                }]}
            />
            <Form.Item>
                <Button
                    type="primary"
                    htmlType="submit"
                    loading={submitting}
                    disabled={uploading}
                    style={{ background: '#7b6ef6', borderColor: '#7b6ef6' }}
                >
                    {submitLabel}
                </Button>
            </Form.Item>
        </Form>
    );
}
